
export default function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-white to-orange-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-6 font-display">
            Get Your Time Back with <strong>Affordable Bookkeeping Services</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Stop spending countless hours on data entry and financial worries. Our professional <strong>QuickBooks bookkeeping service</strong> in El Paso gives you the freedom to focus on what matters most - growing your business and spending time with your family.
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-16">
          <img 
            src="https://public.readdy.ai/ai/img_res/b4e02f9333d924900b4adaca183a8102.jpg"
            alt="el-paso-business-owner-financial-freedom"
            className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Spend Time on Your Business and Family, Not Data Entry
            </h2>
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              As a business owner in El Paso, your time is your most valuable asset. Every minute spent wrestling with QuickBooks or sorting through receipts is time taken away from growing your business or enjoying precious moments with your family. Our <strong>El Paso bookkeepers</strong> understand this challenge intimately, which is why we've designed our services to completely eliminate the burden of financial data management from your daily routine.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              When you partner with our <strong>QuickBooks bookkeeping service</strong>, you're not just hiring someone to enter numbers into a system. You're investing in a comprehensive solution that handles every aspect of your financial record-keeping with precision and expertise. From categorizing transactions and reconciling bank statements to managing accounts payable and receivable, we take care of all the time-consuming tasks that keep you away from what you do best.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-8">
              Our clients consistently report saving 15-20 hours per week once they transition their bookkeeping responsibilities to our team. Imagine what you could accomplish with those extra hours - developing new products, meeting with potential clients, attending your child's soccer game, or simply having a peaceful dinner with your family without the stress of unfinished financial tasks weighing on your mind.
            </p>
            
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <h4 className="font-bold text-teal-800 mb-2">Time Freedom Benefits:</h4>
              <ul className="text-teal-700 text-sm space-y-2">
                <li>• Save 15-20 hours per week on bookkeeping tasks</li>
                <li>• Focus on revenue-generating activities</li>
                <li>• Achieve better work-life balance</li>
                <li>• Reduce stress and mental burden</li>
                <li>• Spend quality time with family</li>
              </ul>
            </div>
          </div>

          <div className="space-y-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Happy%20family%20spending%20quality%20time%20together%20at%20El%20Paso%20park%20with%20mountains%20in%20background%2C%20parents%20playing%20with%20children%2C%20warm%20golden%20hour%20lighting%2C%20lifestyle%20photography%20showing%20work-life%20balance%20achieved%20through%20professional%20business%20management&width=600&height=400&seq=elpaso-family-time-balance&orientation=landscape"
              alt="el-paso-family-work-life-balance"
              className="w-full h-64 object-cover object-top rounded-xl shadow-lg"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20in%20modern%20El%20Paso%20conference%20room%20with%20business%20owner%20confidently%20presenting%20to%20clients%2C%20clean%20organized%20workspace%2C%20professional%20atmosphere%20showing%20successful%20business%20growth%20and%20leadership&width=600&height=300&seq=elpaso-business-growth-meeting&orientation=landscape"
              alt="el-paso-business-growth-success"
              className="w-full h-48 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
        </div>

        {/* Accuracy Section */}
        <div className="mb-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20bookkeeper%20working%20with%20QuickBooks%20software%20on%20computer%20screen%20showing%20accurate%20financial%20data%20and%20organized%20reports%2C%20clean%20modern%20office%20environment%2C%20attention%20to%20detail%20and%20precision%20in%20financial%20record%20keeping&width=600&height=500&seq=quickbooks-accuracy-professional&orientation=portrait"
                alt="quickbooks-bookkeeping-accuracy-el-paso"
                className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How Professional Bookkeeping Keeps Your Records Accurate
              </h2>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                Accuracy in bookkeeping isn't just about having neat numbers in your QuickBooks - it's about having confidence in every financial decision you make. Our <strong>El Paso QuickBooks help</strong> ensures that every transaction is properly categorized, every receipt is accounted for, and every financial record meets the highest standards of accuracy and compliance.
              </p>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                Many business owners attempt to handle their own bookkeeping, often leading to costly mistakes that compound over time. Miscategorized expenses, missed deductions, duplicate entries, and unreconciled accounts can create a domino effect that impacts everything from tax preparation to loan applications. Our professional <strong>QuickBooks bookkeeping service</strong> eliminates these risks through systematic processes and expert oversight.
              </p>
              <p className="text-gray-700 text-base leading-relaxed mb-8">
                We implement a multi-layer review system where every entry is verified, cross-referenced, and validated against source documents. Our team uses advanced QuickBooks features and integrations to automate routine tasks while maintaining human oversight for complex transactions. This approach ensures 99.9% accuracy rates while significantly reducing the time required for monthly closings and financial reporting.
              </p>
              
              <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
                <h4 className="font-bold text-orange-800 mb-3">Accuracy Guarantee:</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-orange-700">Daily Reconciliation</p>
                    <p className="text-orange-600">Bank accounts matched daily</p>
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700">Error Detection</p>
                    <p className="text-orange-600">Automated discrepancy alerts</p>
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700">Document Tracking</p>
                    <p className="text-orange-600">Every receipt catalogued</p>
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700">Audit Ready</p>
                    <p className="text-orange-600">Compliant record keeping</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decision Making Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Make Better Business Decisions with Up-to-Date QuickBooks Information
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Real-time financial insights are the foundation of smart business decisions. Our <strong>El Paso bookkeepers</strong> ensure your QuickBooks data is always current, giving you the clarity needed to navigate challenges and capitalize on opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20dashboard%20showing%20real-time%20financial%20analytics%20and%20QuickBooks%20reports%20on%20modern%20computer%20screen%2C%20clean%20interface%20with%20charts%20and%20graphs%2C%20business%20intelligence%20visualization%20for%20informed%20decision%20making&width=400&height=300&seq=quickbooks-analytics-dashboard&orientation=landscape"
                alt="quickbooks-financial-analytics-reporting"
                className="w-full h-48 object-cover object-top rounded-xl shadow-lg mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Real-Time Reporting</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Access current financial data whenever you need it, enabling quick responses to market changes and business opportunities.
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Business%20owner%20analyzing%20cash%20flow%20trends%20and%20profit%20margins%20on%20tablet%20in%20El%20Paso%20office%2C%20focused%20expression%20showing%20strategic%20thinking%2C%20professional%20environment%20with%20financial%20documents%20and%20charts&width=400&height=300&seq=cash-flow-analysis-elpaso&orientation=landscape"
                alt="cash-flow-analysis-business-decisions"
                className="w-full h-48 object-cover object-top rounded-xl shadow-lg mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cash Flow Insights</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Understand your cash flow patterns to make informed decisions about investments, expenses, and growth opportunities.
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20with%20team%20reviewing%20quarterly%20financial%20performance%20reports%20and%20growth%20projections%2C%20collaborative%20environment%20in%20modern%20El%20Paso%20office%20with%20mountain%20views%2C%20strategic%20planning%20session&width=400&height=300&seq=financial-planning-meeting&orientation=landscape"
                alt="financial-planning-business-growth"
                className="w-full h-48 object-cover object-top rounded-xl shadow-lg mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Growth Planning</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Use accurate financial data to create realistic budgets and growth plans that align with your business goals.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              The Power of Informed Decision Making
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  When your QuickBooks data is maintained by our professional <strong>bookkeeping service</strong>, you gain access to powerful insights that can transform how you run your business. Our clients regularly use their up-to-date financial information to negotiate better terms with suppliers, identify the most profitable aspects of their business, and make strategic decisions about staffing and expansion.
                </p>
                <p className="text-gray-700 text-base leading-relaxed">
                  Consider the El Paso restaurant owner who used our monthly profit and loss reports to identify that their lunch service was actually losing money. With this insight, they restructured their operations and increased profitability by 35% within six months. Or the construction company that used cash flow projections to secure a line of credit before their busy season, ensuring they could take on larger projects without cash flow constraints.
                </p>
              </div>
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-line-chart-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Profit Optimization</h4>
                      <p className="text-gray-600 text-sm">Identify high-margin services and eliminate unprofitable activities</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-funds-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Investment Timing</h4>
                      <p className="text-gray-600 text-sm">Know when to invest in equipment, staff, or expansion</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-shield-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Risk Management</h4>
                      <p className="text-gray-600 text-sm">Spot potential problems before they impact your business</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Affordability Section */}
        <div className="mb-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Affordable Solutions That Fit Your Budget
              </h2>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                Professional bookkeeping shouldn't break your budget. Our <strong>El Paso QuickBooks help</strong> is designed to be more affordable than hiring a full-time bookkeeper, while delivering superior results and peace of mind. We offer flexible pricing plans that scale with your business size and complexity, ensuring you only pay for the services you actually need.
              </p>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                Many business owners are surprised to discover that our professional <strong>QuickBooks bookkeeping service</strong> costs less than they were spending on bookkeeping software, training, and the opportunity cost of their own time. When you factor in the reduction in costly errors, missed deductions, and late fees, our services often pay for themselves within the first month.
              </p>
              <p className="text-gray-700 text-base leading-relaxed mb-8">
                We believe that every El Paso business deserves access to professional financial management, regardless of size or industry. That's why we've structured our services to be accessible to startups and established companies alike, with transparent pricing and no hidden fees or long-term contracts.
              </p>
              
              <div className="bg-gradient-to-r from-teal-50 to-orange-50 p-6 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-800 mb-4">Cost Comparison:</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">$3,500+</p>
                    <p className="text-gray-700">Monthly cost of full-time bookkeeper</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-teal-600">$299+</p>
                    <p className="text-gray-700">Our professional service</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20calculator%20and%20financial%20documents%20showing%20affordable%20bookkeeping%20costs%20and%20budget%20analysis%2C%20clean%20organized%20workspace%20with%20QuickBooks%20software%20visible%2C%20cost-effective%20business%20solutions%20concept&width=600&height=500&seq=affordable-bookkeeping-costs&orientation=portrait"
                alt="affordable-bookkeeping-services-el-paso"
                className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Marketing Services Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-orange-50 to-teal-50 rounded-2xl p-8 border border-orange-200">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Grow Your Business with Our Marketing Services
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Beyond keeping your books in order, we can help you grow your El Paso business with targeted marketing services designed to increase your revenue and expand your customer base.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Digital%20marketing%20strategy%20meeting%20with%20El%20Paso%20business%20team%20reviewing%20growth%20analytics%20on%20computer%20screens%2C%20modern%20office%20environment%20showing%20collaborative%20marketing%20planning%20and%20business%20development&width=600&height=400&seq=marketing-strategy-elpaso&orientation=landscape"
                  alt="el-paso-business-marketing-growth"
                  className="w-full h-64 object-cover object-top rounded-xl shadow-lg"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Comprehensive Growth Solutions
                </h3>
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  Our marketing services complement our <strong>bookkeeping services</strong> by helping you track the return on investment for every marketing dollar spent. We offer digital marketing, local SEO, social media management, and strategic business consulting to help your El Paso business reach its full potential.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-gray-700">Local El Paso SEO optimization</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-gray-700">Social media marketing campaigns</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-gray-700">Website design and optimization</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-gray-700">Marketing ROI tracking and analysis</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Stories Grid */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            El Paso Businesses We've Helped Succeed
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <img 
                src="https://readdy.ai/api/search-image?query=Successful%20El%20Paso%20restaurant%20interior%20with%20busy%20dining%20area%20and%20happy%20customers%2C%20warm%20lighting%20and%20professional%20atmosphere%20showing%20thriving%20local%20business%20success%20through%20proper%20financial%20management&width=400&height=250&seq=restaurant-success-elpaso&orientation=landscape"
                alt="el-paso-restaurant-business-success"
                className="w-full h-32 object-cover object-top rounded-lg mb-4"
              />
              <h4 className="font-bold text-gray-900 mb-2">Local Restaurant</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Increased profitability by 35% after implementing our bookkeeping and cost analysis recommendations.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20construction%20site%20in%20El%20Paso%20with%20modern%20equipment%20and%20organized%20workflow%2C%20successful%20construction%20business%20showing%20growth%20and%20efficiency%20through%20proper%20financial%20management&width=400&height=250&seq=construction-success-elpaso&orientation=landscape"
                alt="el-paso-construction-business-growth"
                className="w-full h-32 object-cover object-top rounded-lg mb-4"
              />
              <h4 className="font-bold text-gray-900 mb-2">Construction Company</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Secured major contracts with accurate financial reporting and improved cash flow management.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20retail%20store%20in%20El%20Paso%20with%20organized%20inventory%20and%20professional%20displays%2C%20successful%20retail%20business%20showing%20growth%20through%20effective%20bookkeeping%20and%20financial%20planning%20strategies&width=400&height=250&seq=retail-success-elpaso&orientation=landscape"
                alt="el-paso-retail-business-success"
                className="w-full h-32 object-cover object-top rounded-lg mb-4"
              />
              <h4 className="font-bold text-gray-900 mb-2">Retail Business</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Expanded to three locations using financial insights and strategic planning from our team.
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-teal-600 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Get Your Time Back?
          </h2>
          <p className="text-xl mb-8 text-teal-100 max-w-3xl mx-auto">
            Join hundreds of satisfied El Paso business owners who have discovered the freedom that comes with professional <strong>QuickBooks bookkeeping services</strong>. Contact us today for your free consultation.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="#contact" 
              className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Start Your Free Consultation
            </a>
            <a 
              href="#contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Learn More About Our Services
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
